// Template/Framework configuration with colors and metadata
// Used for displaying colorful framework tags throughout the app

export interface TemplateConfig {
  id: string;
  name: string;
  icon: string;
  category: 'web' | 'python' | 'mobile' | 'systems' | '3d' | 'devops';
  runtime:
    | 'browser'
    | 'codegen'
    | 'pyodide'
    | 'dartpad'
    | 'rust-playground'
    | 'go-playground'
    | 'express-playground'
    | 'php-playground'
    | 'godot-web'
    | 'unity-web';
  color: string; // CSS color value
  bgColor: string; // Background color (with transparency)
  borderColor: string; // Border color
}

// Single source of truth for all platform/template IDs supported by Taskingtech.
// Keep this list in sync with TEMPLATE_CONFIGS keys.
export const SUPPORTED_PLATFORM_IDS = [
  'vanilla',
  'react',
  'shadcn',
  'vue',
  'next',
  'svelte',
  'solid',
  'angular',
  'express',
  'laravel',
  'python',
  'flask',
  'fastapi',
  'django',
  'flutter',
  'android-shell',
  'ios-shell',
  'mobile-shell',
  'rust',
  'go',
  'three',
  'playcanvas',
  'godot',
  'godot3d',
  'unity',
  'unity-2d',
  'unreal',
  'ci',
] as const;

export type SupportedPlatformId = (typeof SUPPORTED_PLATFORM_IDS)[number];

export interface TemplateThemeSupport {
  tailwind: boolean;
  shadcnUi: boolean;
  radixUi: boolean;
}

const SHADCN_RADIX_PLATFORM_IDS = new Set<SupportedPlatformId>(['react', 'shadcn', 'next']);
const TAILWIND_PLATFORM_IDS = new Set<SupportedPlatformId>([
  'vanilla',
  'react',
  'shadcn',
  'vue',
  'next',
  'svelte',
  'solid',
  'angular',
  'three',
  'playcanvas',
]);

export function getThemeSupportForTemplate(
  id: string | null | undefined,
): TemplateThemeSupport {
  if (!id) return { tailwind: false, shadcnUi: false, radixUi: false };
  const asSupported = id as SupportedPlatformId;
  const shadcnUi = SHADCN_RADIX_PLATFORM_IDS.has(asSupported);
  return {
    tailwind: TAILWIND_PLATFORM_IDS.has(asSupported),
    shadcnUi,
    radixUi: shadcnUi,
  };
}

export const TEMPLATE_CONFIGS: Record<string, TemplateConfig> = {
  // Web Frameworks - Blue/Cyan tones
  vanilla: {
    id: 'vanilla',
    name: 'Vanilla',
    icon: '⚡',
    category: 'web',
    runtime: 'browser',
    color: '#facc15',
    bgColor: 'rgba(250, 204, 21, 0.15)',
    borderColor: 'rgba(250, 204, 21, 0.3)',
  },
  react: {
    id: 'react',
    name: 'React',
    icon: '⚛️',
    category: 'web',
    runtime: 'browser',
    color: '#61dafb',
    bgColor: 'rgba(97, 218, 251, 0.15)',
    borderColor: 'rgba(97, 218, 251, 0.3)',
  },
  shadcn: {
    id: 'shadcn',
    name: 'React + Tailwind + shadcn/ui',
    icon: '🎨',
    category: 'web',
    runtime: 'browser',
    color: '#f59e0b',
    bgColor: 'rgba(245, 158, 11, 0.15)',
    borderColor: 'rgba(245, 158, 11, 0.3)',
  },
  vue: {
    id: 'vue',
    name: 'Vue',
    icon: '🖖',
    category: 'web',
    runtime: 'browser',
    color: '#42b883',
    bgColor: 'rgba(66, 184, 131, 0.15)',
    borderColor: 'rgba(66, 184, 131, 0.3)',
  },
  next: {
    id: 'next',
    name: 'Next.js',
    icon: '▲',
    category: 'web',
    runtime: 'browser',
    color: '#ffffff',
    bgColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: 'rgba(255, 255, 255, 0.25)',
  },
  svelte: {
    id: 'svelte',
    name: 'Svelte',
    icon: '🔥',
    category: 'web',
    runtime: 'browser',
    color: '#ff3e00',
    bgColor: 'rgba(255, 62, 0, 0.15)',
    borderColor: 'rgba(255, 62, 0, 0.3)',
  },
  solid: {
    id: 'solid',
    name: 'SolidJS',
    icon: '🧱',
    category: 'web',
    runtime: 'browser',
    color: '#2c4f7c',
    bgColor: 'rgba(44, 79, 124, 0.15)',
    borderColor: 'rgba(44, 79, 124, 0.3)',
  },
  angular: {
    id: 'angular',
    name: 'Angular',
    icon: '🅰️',
    category: 'web',
    runtime: 'browser',
    color: '#dd0031',
    bgColor: 'rgba(221, 0, 49, 0.15)',
    borderColor: 'rgba(221, 0, 49, 0.3)',
  },
  express: {
    id: 'express',
    name: 'Express',
    icon: '🧩',
    category: 'web',
    runtime: 'browser',
    color: '#68a063',
    bgColor: 'rgba(104, 160, 99, 0.15)',
    borderColor: 'rgba(104, 160, 99, 0.3)',
  },
  laravel: {
    id: 'laravel',
    name: 'Laravel',
    icon: '🔺',
    category: 'web',
    runtime: 'php-playground',
    color: '#ff2d20',
    bgColor: 'rgba(255, 45, 32, 0.15)',
    borderColor: 'rgba(255, 45, 32, 0.3)',
  },

  // Python Frameworks - Yellow/Green tones
  python: {
    id: 'python',
    name: 'Python',
    icon: '🐍',
    category: 'python',
    runtime: 'pyodide',
    color: '#3776ab',
    bgColor: 'rgba(55, 118, 171, 0.15)',
    borderColor: 'rgba(55, 118, 171, 0.3)',
  },
  flask: {
    id: 'flask',
    name: 'Flask',
    icon: '🌶️',
    category: 'python',
    runtime: 'pyodide',
    color: '#000000',
    bgColor: 'rgba(128, 128, 128, 0.15)',
    borderColor: 'rgba(128, 128, 128, 0.3)',
  },
  fastapi: {
    id: 'fastapi',
    name: 'FastAPI',
    icon: '⚡',
    category: 'python',
    runtime: 'pyodide',
    color: '#009688',
    bgColor: 'rgba(0, 150, 136, 0.15)',
    borderColor: 'rgba(0, 150, 136, 0.3)',
  },
  django: {
    id: 'django',
    name: 'Django',
    icon: '🎸',
    category: 'python',
    runtime: 'pyodide',
    color: '#092e20',
    bgColor: 'rgba(9, 46, 32, 0.25)',
    borderColor: 'rgba(9, 46, 32, 0.4)',
  },

  // Mobile - Purple tones
  flutter: {
    id: 'flutter',
    name: 'Flutter',
    icon: '📱',
    category: 'mobile',
    runtime: 'dartpad',
    color: '#02569b',
    bgColor: 'rgba(2, 86, 155, 0.15)',
    borderColor: 'rgba(2, 86, 155, 0.3)',
  },
  'android-shell': {
    id: 'android-shell',
    name: 'Android WebView Shell',
    icon: '🤖',
    category: 'mobile',
    runtime: 'codegen', // Download wrapper project, build locally with Android Studio
    color: '#3ddc84',
    bgColor: 'rgba(61, 220, 132, 0.15)',
    borderColor: 'rgba(61, 220, 132, 0.3)',
  },
  'ios-shell': {
    id: 'ios-shell',
    name: 'iOS WKWebView Shell',
    icon: '🍎',
    category: 'mobile',
    runtime: 'codegen', // Download wrapper project, build locally with Xcode
    color: '#007aff',
    bgColor: 'rgba(0, 122, 255, 0.15)',
    borderColor: 'rgba(0, 122, 255, 0.3)',
  },
  'mobile-shell': {
    id: 'mobile-shell',
    name: 'Mobile App Shell (Android + iOS)',
    icon: '📲',
    category: 'mobile',
    runtime: 'codegen', // Download wrapper projects, build locally
    color: '#9c27b0',
    bgColor: 'rgba(156, 39, 176, 0.15)',
    borderColor: 'rgba(156, 39, 176, 0.3)',
  },

  // Systems Languages - Orange/Rust tones
  rust: {
    id: 'rust',
    name: 'Rust',
    icon: '🦀',
    category: 'systems',
    runtime: 'rust-playground',
    color: '#f74c00',
    bgColor: 'rgba(247, 76, 0, 0.15)',
    borderColor: 'rgba(247, 76, 0, 0.3)',
  },
  go: {
    id: 'go',
    name: 'Go',
    icon: '🐹',
    category: 'systems',
    runtime: 'go-playground',
    color: '#00add8',
    bgColor: 'rgba(0, 173, 216, 0.15)',
    borderColor: 'rgba(0, 173, 216, 0.3)',
  },

  // 3D/Games - Pink/Purple tones
  three: {
    id: 'three',
    name: 'Three.js',
    icon: '🕹️',
    category: '3d',
    runtime: 'browser',
    color: '#049ef4',
    bgColor: 'rgba(4, 158, 244, 0.15)',
    borderColor: 'rgba(4, 158, 244, 0.3)',
  },
  playcanvas: {
    id: 'playcanvas',
    name: 'PlayCanvas',
    icon: '🎮',
    category: '3d',
    runtime: 'browser',
    color: '#ff6600',
    bgColor: 'rgba(255, 102, 0, 0.15)',
    borderColor: 'rgba(255, 102, 0, 0.3)',
  },
  godot: {
    id: 'godot',
    name: 'Godot',
    icon: '🐉',
    category: '3d',
    runtime: 'godot-web',
    color: '#478c5c',
    bgColor: 'rgba(71, 140, 92, 0.15)',
    borderColor: 'rgba(71, 140, 92, 0.3)',
  },
  godot3d: {
    id: 'godot3d',
    name: 'Godot (3D)',
    icon: '🐉',
    category: '3d',
    runtime: 'godot-web',
    color: '#478c5c',
    bgColor: 'rgba(71, 140, 92, 0.15)',
    borderColor: 'rgba(71, 140, 92, 0.3)',
  },

  // Game Engines - Orange/Yellow tones
  unity: {
    id: 'unity',
    name: 'Unity',
    icon: '🎮',
    category: '3d',
    runtime: 'unity-web', // WebGL export for browser preview
    color: '#ffffff',
    bgColor: 'rgba(35, 35, 35, 0.15)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  'unity-2d': {
    id: 'unity-2d',
    name: 'Unity (2D)',
    icon: '🕹️',
    category: '3d',
    runtime: 'unity-web',
    color: '#ffffff',
    bgColor: 'rgba(35, 35, 35, 0.15)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  unreal: {
    id: 'unreal',
    name: 'Unreal Engine',
    icon: '🏗️',
    category: '3d',
    runtime: 'codegen', // Download project, build locally with UE
    color: '#0e1126',
    bgColor: 'rgba(14, 17, 38, 0.15)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },

  // DevOps - Neutral/Gray tones
  ci: {
    id: 'ci',
    name: 'CI/CD',
    icon: '🔁',
    category: 'devops',
    runtime: 'codegen',
    color: '#2088ff',
    bgColor: 'rgba(32, 136, 255, 0.15)',
    borderColor: 'rgba(32, 136, 255, 0.3)',
  },
};

export function getTemplateConfig(templateId: string | null | undefined): TemplateConfig | null {
  if (!templateId) return null;
  return TEMPLATE_CONFIGS[templateId] ?? null;
}

/** Returns true for templates that are codegen-only (no live preview). */
export function isCodegenTemplate(id: string | null | undefined): boolean {
  if (!id || id === 'none') return false;
  const config = TEMPLATE_CONFIGS[id];
  return config?.runtime === 'codegen';
}

/** Returns all template IDs that are codegen-only. */
export function getCodegenTemplateIds(): string[] {
  return Object.keys(TEMPLATE_CONFIGS).filter((id) => TEMPLATE_CONFIGS[id].runtime === 'codegen');
}

// Category display names and colors
export const CATEGORY_CONFIGS: Record<string, { name: string; color: string }> = {
  web: { name: 'Web', color: '#61dafb' },
  python: { name: 'Python', color: '#3776ab' },
  mobile: { name: 'Mobile', color: '#02569b' },
  systems: { name: 'Systems', color: '#f74c00' },
  '3d': { name: '3D/Games', color: '#ff6600' },
  devops: { name: 'DevOps', color: '#2088ff' },
};
